import { v4 as uuid } from "uuid";

import {
  getFBC,
  getFBP,
  getGCLID,
  getLiFatId,
  getRefCode,
  getUserRefCode,
} from "@brexhq/next/src/telemetry/attributionMetaData";
import { getAnonymousId } from "@brexhq/next/src/telemetry/initAnalytics";
import {
  logGetFormRequestFailed,
  logPushLeadsRequestFailed,
} from "@brexhq/next/src/telemetry/logging";
import {
  CreateLeadInput,
  UpdateLeadInput,
  PushMarketoLeadsInput,
} from "@brexhq/next/types/brex-api";
import { ENV_KEYS } from "@brexhq/shared/utils/env/envVariables";

import { getApiEndpoint } from "../utils/endpoints";

interface BaseMarketoForm {
  email?: string;
  country?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  referrerFirstName?: string;
  referrerLastName?: string;
  referrerCompany?: string;
  referrerReferralURL?: string;
}

export interface LeadRequestBody<T = BaseMarketoForm> {
  programName: string;
  source?: string;
  reason?: string;
  lookupField: string;
  input: Array<T>;
}

const postRequest = async (
  url: string,
  body: Record<string, any>,
  headers: Headers,
): Promise<any> => {
  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  return response.json();
};

const getWebsiteHeader = () => {
  const WEBSITE_ACCESS_TOKEN = ENV_KEYS.DASHBOARD_LOCALHOST_TOKEN;

  if (!WEBSITE_ACCESS_TOKEN)
    throw new Error("Dashboard access token environment key undefined");

  const websiteHeader = {
    "Content-Type": "application/json",
    // "x-localhost-website-access": WEBSITE_ACCESS_TOKEN,
  };

  return websiteHeader;
};

const getFormWithFormId = async (formId: number) => {
  const response = await postRequest(
    getApiEndpoint(),
    {
      query: `
        query getFormById($id: SafeString!) {
          marketoLeadForm(marketoFormId: $id) {
            formJson
          }
        }
      `,
      variables: {
        id: formId.toString(),
      },
    },
    new Headers(getWebsiteHeader()),
  );
  if (response.errors) {
    return {
      success: false,
      error: response.errors[0].message,
    };
  }
  return {
    success: true,
    result: JSON.parse(response.data.marketoLeadForm.formJson).result,
  };
};

export const getForm = async (formId: number) => {
  try {
    const formResponse = await getFormWithFormId(formId);
    return formResponse;
  } catch (error) {
    if (error instanceof Error) {
      logGetFormRequestFailed(formId, error.message);
    }
    return { success: false, error: "unexpected error" };
  }
};

const pushLeadRequest = async (
  { input, ...leadData }: LeadRequestBody,
  formId: number,
) => {
  const payload: PushMarketoLeadsInput = {
    ...leadData,
    clientMutationId: uuid(),
    formId: formId.toString(),
    leadAttributionMetadata: {
      fbc: getFBC(),
      fbp: getFBP(),
      gclid: getGCLID(),
      liFatId: getLiFatId(),
      fullUrl: location.href,
      referralCode: getRefCode() ?? getUserRefCode() ?? null,
      segmentAnonymousId: getAnonymousId(),
    },
    leads: input.map((data) => JSON.stringify(data)),
  };
  const response = await postRequest(
    getApiEndpoint(),
    {
      query: `
        mutation pushLeads($input: PushMarketoLeadsInput!) {
          pushMarketoLeads(input: $input) {
            success
          }
        }
      `,
      variables: {
        input: payload,
      },
    },
    new Headers(getWebsiteHeader()),
  );
  if (response.errors) {
    return {
      success: false,
      error: response.errors[0].message,
    };
  }
  return response.data.pushMarketoLeads.success === true
    ? {
        success: true,
      }
    : {
        success: false,
        error: "unexpected error",
      };
};

export const pushLeads = async <T extends BaseMarketoForm>(
  leadData: LeadRequestBody<T>,
  formId: number,
) => {
  try {
    const formResponse = await pushLeadRequest(leadData, formId);
    return formResponse;
  } catch (error) {
    if (error instanceof Error) {
      logPushLeadsRequestFailed(formId, error.message);
    }
    return { success: false, error: "unexpected error" };
  }
};

export const postToApi = (query: string, variables: any) => {
  return postRequest(
    getApiEndpoint(),
    { query, variables },
    new Headers(getWebsiteHeader()),
  );
};

export const createLeadMutation = async (input: CreateLeadInput) => {
  return postToApi(
    `
        mutation createLead($input: CreateLeadInput!) {
          createLead(input: $input) {
            lead {
              id
              leadToken
              salesSegment
            }
          }
        }
      `,
    {
      input,
    },
  );
};

export const updateLeadMutation = async (input: UpdateLeadInput) => {
  return postToApi(
    `
        mutation updateLead($input: UpdateLeadInput!) {
          updateLead(input: $input) {
            lead {
              id
              leadToken
              salesSegment
            }
          }
        }
      `,
    { input },
  );
};

export const getLeadQuery = async (leadToken: string) => {
  return postToApi(
    `
      query GetLeadQuery($id: ID, $leadToken: String!) {
        lead(id: $id, leadToken: $leadToken){
          id
          leadToken
          leadFormStatus
          leadDetails {
            ... on VbMmSalesLeadDetailsObject {
              firstName
              lastName
              email
              phoneNumber
              companyName
              selfAttestedSource
              employeeCount
              fundingSources
              salesVolumeV2
              leadSelfAttestedType
              segmentationDataV3 {
                companyType
                employeeCount
                fundingSources
                salesVolume
              }
            }
          }
        }
      }
  `,
    { leadToken },
  );
};

export const searchIndustriesQuery = async (
  keywords: string,
  limit: number,
) => {
  return postToApi(
    `
      query SearchIndustriesQuery($keywords: String!, $limit: Int!) {
        searchIndustries(keywords: $keywords, first: $limit) {
          edges {
            node {
              industry {
                id
                displayName
                internalName
              }
            }
          }
        }
      }
    `,
    { keywords, limit },
  );
};
